import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import {
  getUserPreferences,
  createUserPreferences,
  updateUserPreferences,
  type UserPreferencesInput,
} from '../services/preferences';

// Infer preferences type from the service return type
type PreferencesData = Awaited<ReturnType<typeof getUserPreferences>>;

/**
 * React Query hook for user preferences
 *
 * Features:
 * - Automatic fetching when user is authenticated
 * - Optimistic updates for better UX
 * - Error handling with retry logic
 * - Cache invalidation on mutations
 *
 * Usage:
 *   const { preferences, isLoading, updatePreferences } = usePreferences();
 *   updatePreferences.mutate({ theme: 'DARK' });
 */

export function usePreferences() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Query key for this user's preferences
  const queryKey = ['preferences', user?.id];

  // Fetch preferences
  const {
    data: preferences,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return getUserPreferences(user.id);
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Create preferences mutation
  const createMutation = useMutation({
    mutationFn: (input: Partial<UserPreferencesInput>) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return createUserPreferences(user.id, input);
    },
    onSuccess: (data) => {
      // Update cache with new data
      queryClient.setQueryData(queryKey, data);
    },
    onError: () => {
      // Failed to create preferences (suppressed)
    },
  });

  // Update preferences mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<UserPreferencesInput>) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return updateUserPreferences(user.id, updates);
    },
    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousPreferences = queryClient.getQueryData(queryKey);

      // Optimistically update cache
      queryClient.setQueryData(queryKey, (old: PreferencesData | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      return { previousPreferences };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousPreferences) {
        queryClient.setQueryData(queryKey, context.previousPreferences);
      }
      // Failed to update preferences (suppressed)
    },
    onSuccess: (data) => {
      // Update cache with server data
      queryClient.setQueryData(queryKey, data);
    },
    onSettled: () => {
      // Refetch to ensure cache is in sync
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Helper to initialize preferences if they don't exist
  const initializePreferences = async (defaults?: Partial<UserPreferencesInput>) => {
    if (!preferences) {
      return createMutation.mutateAsync(defaults || {});
    }
    return preferences;
  };

  return {
    // Data
    preferences,
    hasPreferences: !!preferences,

    // Loading states
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,

    // Error states
    error,
    createError: createMutation.error,
    updateError: updateMutation.error,

    // Mutations
    createPreferences: createMutation.mutate,
    updatePreferences: updateMutation.mutate,
    initializePreferences,

    // Async versions for promise handling
    createPreferencesAsync: createMutation.mutateAsync,
    updatePreferencesAsync: updateMutation.mutateAsync,

    // Utilities
    refetch,
  };
}

/**
 * Hook for specific preference values with type safety
 * Useful for granular subscriptions to avoid unnecessary re-renders
 *
 * Usage:
 *   const theme = usePreferenceValue('theme', 'AUTO');
 */
export function usePreferenceValue<K extends keyof UserPreferencesInput>(
  key: K,
  defaultValue: UserPreferencesInput[K]
): UserPreferencesInput[K] {
  const { preferences } = usePreferences();

  if (!preferences) {
    return defaultValue;
  }

  return (preferences[key] as UserPreferencesInput[K]) ?? defaultValue;
}
